import { createTheme, type ThemeOptions } from '@mui/material/styles';

// Vapas / BRAMBO design-system tokens (docs/change-requests/CHR-01/DESIGN.md
// §2/§3/§4 — adopted as canonical via ADR-0029). Kept as named constants —
// no raw `#hex` outside this file (status palettes excepted, see DESIGN.md §2;
// they still live here, housed in `theme.statusPalette`).
const TOKENS = {
  primary: '#3F7A3F', // green — řepa / pole
  primarySoft: 'rgba(63, 122, 63, 0.1)', // nav-active bg / chip-soft brand
  primaryStrong: '#356B35', // hover
  // `secondary` = earthy brown (DESIGN.md `--secondary`). Used for neutral
  // chrome accents the design designates (avatar bg variants etc). NOT a
  // marketing brand-secondary.
  secondary: '#8B6F47', // earthy brown
  secondarySoft: 'rgba(139, 111, 71, 0.15)',
  secondaryStrong: '#5E4B30', // derived darker brown (≈ -30% lightness)
  accent: '#E59235', // orange — accent CTAs only (NOT destructive delete)
  bg: '#FAF7F0', // warm beige app background
  rowHover: '#FBF9F4', // DESIGN.md §7 — table hover row (one notch < bg)
  paper: '#FFFFFF',
  text: '#1F2937', // primary text
  textMuted: '#6B7280', // secondary text / labels
  textFaint: '#9CA3AF', // derived from #6B7280 — disabled / faint
  border: '#E5E0D5', // warm-grey border
  borderStrong: '#C8C0AE', // stronger border / outlined-hover
  success: '#16A34A',
  warning: '#F59E0B',
  error: '#DC2626',
  info: '#0284C7',
  fontSans: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontMono:
    '"JetBrains Mono", ui-monospace, "SF Mono", Consolas, "Liberation Mono", monospace',
} as const;

// Status chip palettes (DESIGN.md §2 — raw hex exempt, but theme-housed so
// components read `theme.statusPalette[...]` rather than scattering hex).
// Each entry = { bg, color } pair for a chip/pill stat.
const STATUS_PALETTE = {
  planned: { bg: '#DBEAFE', color: '#1D4ED8' }, // Plánováno
  inProgress: { bg: '#FFEDD5', color: '#C2410C' }, // Probíhá
  done: { bg: '#DCFCE7', color: '#15803D' }, // Hotovo
  paused: { bg: '#FEE2E2', color: '#B91C1C' }, // Pozastaveno
  waiting: { bg: '#FEF3C7', color: '#B45309' }, // Čeká na obec
  active: { bg: '#DCFCE7', color: '#15803D' }, // Aktivní
  // "Moje jízda" — the logged-in user's own assignment, highlighted amber/yellow
  // app-wide (calendar cell, list dispatcher label, detail dispatcher label).
  // SINGLE yellow source for the my-jizdy highlight; no per-site hex. Amber-50
  // bg / amber-700 text reads legibly on the beige (#FAF7F0) app bg + white
  // cards, matching the palette's bg/color contrast convention.
  mine: { bg: '#FEF9C3', color: '#A16207' }, // Moje
  inactive: { bg: '#F3F4F6', color: '#4B5563' }, // Neaktivní
  service: { bg: '#FEE2E2', color: '#B91C1C' }, // V servisu
  closed: { bg: '#F3F4F6', color: '#4B5563' }, // Uzavřená
} as const;

// 3-level Vapas shadow system (DESIGN.md §4). MUI's `shadows` is a fixed
// 25-entry array, so the Vapas tokens are carried as a typed augmentation
// (precedent: `monospaceFontFamily` below). shadow1 = resting cards,
// shadow2 = hover/selected, shadow3 = modals.
const ELEVATION = {
  shadow1: '0 1px 2px rgba(31,41,55,.06), 0 1px 3px rgba(31,41,55,.04)',
  shadow2: '0 4px 12px rgba(31,41,55,.08), 0 2px 4px rgba(31,41,55,.04)',
  shadow3: '0 12px 32px rgba(31,41,55,.12), 0 4px 8px rgba(31,41,55,.06)',
} as const;

// Table hover-row background (DESIGN.md §7). Module-scope constant so the
// MuiTableRow override can read it without `theme.*` indirection — it is not a
// palette colour, just chrome shading.
const VAPAS_ROW_HOVER = TOKENS.rowHover;

type StatusPalette = typeof STATUS_PALETTE;
type ElevationTokens = typeof ELEVATION;

// Re-exported so feature code can reference `theme.monospaceFontFamily`,
// `theme.statusPalette`, `theme.elevation`, `theme.accent` etc. via the
// theme object — never a raw value in components.
declare module '@mui/material/styles' {
  interface Theme {
    monospaceFontFamily: string;
    statusPalette: StatusPalette;
    elevation: ElevationTokens;
    accent: string;
    borderStrong: string;
  }
  interface ThemeOptions {
    monospaceFontFamily?: string;
    statusPalette?: StatusPalette;
    elevation?: ElevationTokens;
    accent?: string;
    borderStrong?: string;
  }
}

const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: TOKENS.primary,
      light: TOKENS.primarySoft,
      dark: TOKENS.primaryStrong,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: TOKENS.secondary,
      light: TOKENS.secondarySoft,
      dark: TOKENS.secondaryStrong,
      contrastText: '#FFFFFF',
    },
    success: { main: TOKENS.success, contrastText: '#FFFFFF' },
    warning: { main: TOKENS.warning, contrastText: '#FFFFFF' },
    error: { main: TOKENS.error, contrastText: '#FFFFFF' },
    info: { main: TOKENS.info, contrastText: '#FFFFFF' },
    background: {
      default: TOKENS.bg,
      paper: TOKENS.paper,
    },
    text: {
      primary: TOKENS.text,
      secondary: TOKENS.textMuted,
      disabled: TOKENS.textFaint,
    },
    divider: TOKENS.border,
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: TOKENS.fontSans,
    fontSize: 14,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    button: {
      textTransform: 'none', // Czech UI — never uppercase buttons.
      fontWeight: 500,
    },
    h1: { fontFamily: TOKENS.fontSans, fontWeight: 600 },
    h2: { fontFamily: TOKENS.fontSans, fontWeight: 600 },
    h3: { fontFamily: TOKENS.fontSans, fontWeight: 600 },
    h4: { fontFamily: TOKENS.fontSans, fontWeight: 600 },
    h5: { fontFamily: TOKENS.fontSans, fontWeight: 600 },
    h6: { fontFamily: TOKENS.fontSans, fontWeight: 600 },
  },
  monospaceFontFamily: TOKENS.fontMono,
  statusPalette: STATUS_PALETTE,
  elevation: ELEVATION,
  accent: TOKENS.accent,
  borderStrong: TOKENS.borderStrong,
  components: {
    // Global `tabular-nums` (DESIGN.md §3 — non-negotiable for all numbers).
    // Applied on body via font-feature-settings; numeric cells can additionally
    // opt into `fontVariantNumeric: 'tabular-nums'` where needed.
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFeatureSettings: '"tnum" 1',
          fontVariantNumeric: 'tabular-nums',
        },
        // CHR-04 §14 — the Safari „prázdné pole ukazuje 12:30" bug.
        //
        // ROOT CAUSE (reproduced on the Playwright `webkit` project, see
        // `e2e/web/specs/safari-time-inputs.spec.ts`): WebKit paints the
        // PLACEHOLDER of an empty `<input type=time>` as a sample value —
        // `12:30` in a 24-hour locale, `12:30 PM` in en-US — where Chrome and
        // Firefox paint `--:--`. An empty `type=date` gets today's date the
        // same way. It is engine-level: a bare input with no attributes and no
        // MUI wrapper does it, and it is unaffected by `step`/`min`/`max` or by
        // `autocomplete`. The value is NOT real — `.value` stays `''`, a
        // focus+blur commits nothing, and a partially-typed field still reads
        // `''` — so nothing wrong was ever saved. The damage is purely that a
        // dispatcher cannot tell "no time recorded" from "12:30".
        //
        // FIX: hide the placeholder on a field the app knows is empty. The
        // `data-empty` attribute is stamped by the component (React owns the
        // value; CSS has no selector for an empty date/time input —
        // `:placeholder-shown` does not match them in either engine), and
        // `:not(:focus)` lifts the mask the moment the user starts editing.
        //
        // The whole rule is behind `@supports (-webkit-hyphens: none)`, which
        // matches WebKit and NOT Blink (asserted on both projects). Chrome's
        // `--:--` is genuinely useful and must stay exactly as the mockup shows
        // it — this is a WebKit-only repair, not a cross-browser restyle.
        '@supports (-webkit-hyphens: none)': {
          'input[type="time"][data-empty="true"]:not(:focus)::-webkit-datetime-edit, input[type="date"][data-empty="true"]:not(:focus)::-webkit-datetime-edit':
            {
              opacity: 0,
            },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          minHeight: 44, // tap target — UI-02.7 / web on tablet
          paddingInline: 16,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
    },
    // The floated label sits in a notch cut out of the outlined border. MUI's
    // default legend width derives from `fontSize: 0.75em`, which under the Inter
    // font undersizes the notch — the top border line then strikes through the
    // floated label ("text is crossed out by the cell outline"). Widen the legend
    // so the notch fully clears the label on every outlined field at once.
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          legend: {
            fontSize: '0.85em',
          },
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        color: 'inherit',
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    // DESIGN.md §5 TopBar `height: 56px`. MUI's default `Toolbar` ships a
    // desktop media-query rule `minHeight: 64` that wins over an inline `sx`
    // value on the instance. Pin it from the theme so every Toolbar honours
    // the chrome height.
    MuiToolbar: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 56,
          [theme.breakpoints.up('xs')]: { minHeight: 56 },
          [theme.breakpoints.up('sm')]: { minHeight: 56 },
        }),
      },
    },
    // DESIGN.md §4 — cards/modals radius 12.
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 12,
          boxShadow: theme.elevation.shadow1,
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: 12,
          boxShadow: theme.elevation.shadow3,
        }),
      },
    },
    // DESIGN.md §6.4 — status pills/chips radius 999.
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 999,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 44,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    // DESIGN.md §7 — Vapas data-table styling applied at the theme level so
    // EVERY `<Table>` (orders here, admin/audit in Phase 2b) inherits it for
    // free. Header = uppercase 11.5px 600 on beige `background.default`,
    // letter-spacing 0.04em; cells = 12/14px padding with a bottom border;
    // hover rows lighten to `#FBF9F4` (one notch lighter than the beige app
    // bg). Numeric columns opt in via MUI's `align="right"` which also gets
    // `tabular-nums` here so callers don't repeat it per cell.
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
          padding: theme.spacing(1.5, 1.75), // 12px / 14px (DESIGN.md §7)
          fontSize: 13,
          verticalAlign: 'middle',
        }),
        head: ({ theme }) => ({
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.secondary,
          fontSize: 11.5,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          whiteSpace: 'nowrap',
        }),
        // Right-aligned cells are numeric per DESIGN.md §7 → tabular-nums.
        alignRight: {
          fontVariantNumeric: 'tabular-nums',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:hover': {
            backgroundColor: VAPAS_ROW_HOVER,
          },
          // The hover lightening must also reach a `stickyHeader` head row's
          // cells; scope the hover to body rows by excluding head context.
          '.MuiTableHead-root &:hover': {
            backgroundColor: theme.palette.background.default,
          },
        }),
      },
    },
  },
};

export const theme = createTheme(themeOptions);

export type AppTheme = typeof theme;
